import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Plus,
  Download,
  FileText,
  Package,
  AlertTriangle,
  Clock,
  XCircle,
  Thermometer,
  MapPin,
  User,
  Hash,
  Calendar,
  Beaker,
  StickyNote,
  ArrowDown,
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { useTableExport } from '@/hooks/useTableExport';

// ─── Types ───────────────────────────────────────────────────────────────────

interface InventoryItem extends Record<string, unknown> {
  id: string;
  itemCode: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  minStock: number;
  maxStock: number;
  expiryDate: string;
  location: string;
  supplier: string;
  lotNo: string;
  storageTemp: string;
  status: 'available' | 'low_stock' | 'critical' | 'expired';
  lastReceivedDate: string;
  usageNotes?: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_INVENTORY: InventoryItem[] = [
  {
    id: '1',
    itemCode: 'INV-001',
    name: 'Acetonitrile HPLC Grade',
    category: 'Solvent',
    quantity: 4000,
    unit: 'mL',
    minStock: 1000,
    maxStock: 10000,
    expiryDate: '2026-08-15',
    location: 'Flammables Cabinet',
    supplier: 'Merck KGaA',
    lotNo: 'K53021847',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-02-10',
    usageNotes: 'Use for HPLC mobile phase. Wear appropriate PPE.',
  },
  {
    id: '2',
    itemCode: 'INV-002',
    name: 'Methanol LC-MS Grade',
    category: 'Solvent',
    quantity: 900,
    unit: 'mL',
    minStock: 1000,
    maxStock: 8000,
    expiryDate: '2026-11-30',
    location: 'Flammables Cabinet',
    supplier: 'Honeywell Burdick & Jackson',
    lotNo: 'BJ2024-5521',
    storageTemp: '15–25°C',
    status: 'low_stock',
    lastReceivedDate: '2024-12-05',
    usageNotes: 'Low stock — raise PO before depletion.',
  },
  {
    id: '3',
    itemCode: 'INV-003',
    name: 'Amoxicillin Reference Standard',
    category: 'Reference Standard',
    quantity: 200,
    unit: 'mg',
    minStock: 100,
    maxStock: 500,
    expiryDate: '2025-06-30',
    location: 'Refrigerator 2-8°C',
    supplier: 'USP',
    lotNo: 'R08A0296',
    storageTemp: '2–8°C',
    status: 'available',
    lastReceivedDate: '2024-07-01',
    usageNotes: 'Potency: 900 µg/mg. Store desiccated.',
  },
  {
    id: '4',
    itemCode: 'INV-004',
    name: 'Paracetamol Reference Standard',
    category: 'Reference Standard',
    quantity: 150,
    unit: 'mg',
    minStock: 100,
    maxStock: 500,
    expiryDate: '2025-04-30',
    location: 'Refrigerator 2-8°C',
    supplier: 'EP Reference Standards',
    lotNo: 'EP-PARA-2302',
    storageTemp: '2–8°C',
    status: 'critical',
    lastReceivedDate: '2024-05-10',
    usageNotes: 'Critical expiry — initiate reorder immediately.',
  },
  {
    id: '5',
    itemCode: 'INV-005',
    name: 'Potassium Dihydrogen Phosphate',
    category: 'Reagent',
    quantity: 500,
    unit: 'g',
    minStock: 200,
    maxStock: 2000,
    expiryDate: '2027-03-20',
    location: 'Chemical Store A-3',
    supplier: 'Fisher Scientific',
    lotNo: 'FS-21934-B',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-01-18',
    usageNotes: 'Analytical grade. Used in buffer preparation.',
  },
  {
    id: '6',
    itemCode: 'INV-006',
    name: 'Sodium Hydroxide (NaOH)',
    category: 'Reagent',
    quantity: 1200,
    unit: 'g',
    minStock: 500,
    maxStock: 5000,
    expiryDate: '2027-06-15',
    location: 'Chemical Store A-3',
    supplier: 'Sigma-Aldrich',
    lotNo: 'SLBM3310V',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-03-01',
    usageNotes: 'Keep tightly sealed. Hygroscopic.',
  },
  {
    id: '7',
    itemCode: 'INV-007',
    name: 'Hydrochloric Acid 37%',
    category: 'Reagent',
    quantity: 250,
    unit: 'mL',
    minStock: 500,
    maxStock: 3000,
    expiryDate: '2026-12-01',
    location: 'Chemical Store A-3',
    supplier: 'Merck KGaA',
    lotNo: 'K2409302144',
    storageTemp: '15–25°C',
    status: 'low_stock',
    lastReceivedDate: '2024-10-22',
    usageNotes: 'Corrosive. Use in fume hood only.',
  },
  {
    id: '8',
    itemCode: 'INV-008',
    name: 'Milli-Q Water',
    category: 'Solvent',
    quantity: 10000,
    unit: 'mL',
    minStock: 5000,
    maxStock: 50000,
    expiryDate: '2026-04-30',
    location: 'Chemical Store A-3',
    supplier: 'In-house (Millipore)',
    lotNo: 'MQ-2026-04',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2026-04-01',
    usageNotes: 'Freshly generated. Use within 48 hours of generation.',
  },
  {
    id: '9',
    itemCode: 'INV-009',
    name: 'Amber Vials 2 mL (Silanized)',
    category: 'Consumable',
    quantity: 80,
    unit: 'pcs',
    minStock: 100,
    maxStock: 1000,
    expiryDate: '2028-01-01',
    location: 'Dry Store B-1',
    supplier: 'Thermo Fisher Scientific',
    lotNo: 'TF-AV2-0924',
    storageTemp: '15–25°C',
    status: 'low_stock',
    lastReceivedDate: '2024-09-15',
    usageNotes: 'For LC-MS injections. Discard if scratched.',
  },
  {
    id: '10',
    itemCode: 'INV-010',
    name: 'HPLC Column C18 (150 × 4.6 mm)',
    category: 'Consumable',
    quantity: 3,
    unit: 'pcs',
    minStock: 2,
    maxStock: 10,
    expiryDate: '2027-09-30',
    location: 'Dry Store B-1',
    supplier: 'Waters Corporation',
    lotNo: 'WC18-2024-L1',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2024-11-10',
    usageNotes: 'Flush with 95% MeOH after use. Log injection count.',
  },
  {
    id: '11',
    itemCode: 'INV-011',
    name: 'Mobile Phase Buffer pH 4.5',
    category: 'Buffer',
    quantity: 2000,
    unit: 'mL',
    minStock: 500,
    maxStock: 5000,
    expiryDate: '2026-05-15',
    location: 'Refrigerator 2-8°C',
    supplier: 'In-house',
    lotNo: 'BUF-PH45-0425',
    storageTemp: '2–8°C',
    status: 'available',
    lastReceivedDate: '2026-04-05',
    usageNotes: 'Prepare fresh every 7 days. Verify pH before use.',
  },
  {
    id: '12',
    itemCode: 'INV-012',
    name: 'Isopropanol (IPA) HPLC Grade',
    category: 'Solvent',
    quantity: 3500,
    unit: 'mL',
    minStock: 1000,
    maxStock: 8000,
    expiryDate: '2026-10-20',
    location: 'Flammables Cabinet',
    supplier: 'Sigma-Aldrich',
    lotNo: 'SHBK7162V',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-04-01',
    usageNotes: 'Flammable. Keep away from ignition sources.',
  },
  {
    id: '13',
    itemCode: 'INV-013',
    name: 'Acetone HPLC Grade',
    category: 'Solvent',
    quantity: 500,
    unit: 'mL',
    minStock: 1000,
    maxStock: 5000,
    expiryDate: '2026-07-10',
    location: 'Flammables Cabinet',
    supplier: 'Fisher Scientific',
    lotNo: 'FS-ACE-2024',
    storageTemp: '15–25°C',
    status: 'low_stock',
    lastReceivedDate: '2024-08-20',
    usageNotes: 'Low stock. Reorder in progress.',
  },
  {
    id: '14',
    itemCode: 'INV-014',
    name: 'Disodium Hydrogen Phosphate',
    category: 'Reagent',
    quantity: 800,
    unit: 'g',
    minStock: 300,
    maxStock: 3000,
    expiryDate: '2027-04-01',
    location: 'Chemical Store A-3',
    supplier: 'Merck KGaA',
    lotNo: 'K2401103245',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-01-25',
    usageNotes: 'For phosphate buffer preparation.',
  },
  {
    id: '15',
    itemCode: 'INV-015',
    name: 'Sodium Chloride (NaCl) Analytical Grade',
    category: 'Reagent',
    quantity: 2000,
    unit: 'g',
    minStock: 500,
    maxStock: 5000,
    expiryDate: '2028-06-30',
    location: 'Chemical Store A-3',
    supplier: 'Sigma-Aldrich',
    lotNo: 'WXBD5543V',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-02-14',
    usageNotes: 'Analytical grade. Store in dry conditions.',
  },
  {
    id: '16',
    itemCode: 'INV-016',
    name: 'Formic Acid 98%',
    category: 'Reagent',
    quantity: 100,
    unit: 'mL',
    minStock: 200,
    maxStock: 1000,
    expiryDate: '2025-03-15',
    location: 'Chemical Store A-3',
    supplier: 'Sigma-Aldrich',
    lotNo: 'SHBJ6230V',
    storageTemp: '15–25°C',
    status: 'expired',
    lastReceivedDate: '2024-03-10',
    usageNotes: 'EXPIRED — do not use. Arrange for disposal.',
  },
  {
    id: '17',
    itemCode: 'INV-017',
    name: 'Ammonium Acetate LC-MS Grade',
    category: 'Reagent',
    quantity: 50,
    unit: 'g',
    minStock: 50,
    maxStock: 300,
    expiryDate: '2026-09-01',
    location: 'Chemical Store A-3',
    supplier: 'Sigma-Aldrich',
    lotNo: 'SHBM5102V',
    storageTemp: '15–25°C',
    status: 'critical',
    lastReceivedDate: '2024-09-05',
    usageNotes: 'LC-MS grade. Prepare fresh solution as needed.',
  },
  {
    id: '18',
    itemCode: 'INV-018',
    name: 'Tryptic Soy Broth (TSB) Growth Media',
    category: 'Media',
    quantity: 1500,
    unit: 'g',
    minStock: 500,
    maxStock: 5000,
    expiryDate: '2026-12-31',
    location: 'Dry Store B-1',
    supplier: 'HiMedia Laboratories',
    lotNo: 'HM-TSB-2024-B',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-01-08',
    usageNotes: 'For aerobic organism cultivation. Autoclave at 121°C / 15 min.',
  },
  {
    id: '19',
    itemCode: 'INV-019',
    name: 'Sabouraud Dextrose Agar (SDA) Media',
    category: 'Media',
    quantity: 600,
    unit: 'g',
    minStock: 300,
    maxStock: 2000,
    expiryDate: '2026-08-20',
    location: 'Dry Store B-1',
    supplier: 'HiMedia Laboratories',
    lotNo: 'HM-SDA-2024-A',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2024-12-10',
    usageNotes: 'For yeast and mould growth. pH 5.6 ± 0.2.',
  },
  {
    id: '20',
    itemCode: 'INV-020',
    name: 'Peptone Water',
    category: 'Media',
    quantity: 200,
    unit: 'g',
    minStock: 300,
    maxStock: 2000,
    expiryDate: '2026-06-15',
    location: 'Dry Store B-1',
    supplier: 'HiMedia Laboratories',
    lotNo: 'HM-PW-2024-C',
    storageTemp: '15–25°C',
    status: 'low_stock',
    lastReceivedDate: '2024-11-20',
    usageNotes: 'Diluent for microbial enumeration. Prepare and autoclave before use.',
  },
  {
    id: '21',
    itemCode: 'INV-021',
    name: 'Ciprofloxacin Reference Standard',
    category: 'Reference Standard',
    quantity: 50,
    unit: 'mg',
    minStock: 50,
    maxStock: 300,
    expiryDate: '2026-02-28',
    location: 'Cold Room -20°C',
    supplier: 'USP',
    lotNo: 'R08B0447',
    storageTemp: '-20°C',
    status: 'critical',
    lastReceivedDate: '2024-03-01',
    usageNotes: 'Potency certified. Store frozen. Allow to equilibrate before weighing.',
  },
  {
    id: '22',
    itemCode: 'INV-022',
    name: 'Trifluoroacetic Acid (TFA) LC-MS',
    category: 'Solvent',
    quantity: 5000,
    unit: 'mL',
    minStock: 500,
    maxStock: 10000,
    expiryDate: '2027-01-10',
    location: 'Flammables Cabinet',
    supplier: 'Honeywell Burdick & Jackson',
    lotNo: 'BJ2024-TFA-01',
    storageTemp: '15–25°C',
    status: 'available',
    lastReceivedDate: '2025-01-10',
    usageNotes: 'Ion-pairing reagent. Handle in fume hood. Corrosive.',
  },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ['Reagent', 'Reference Standard', 'Solvent', 'Consumable', 'Media', 'Buffer'];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'low_stock', label: 'Low Stock' },
  { value: 'critical', label: 'Critical' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_PILL_MAP: Record<string, string> = {
  available: 'available',
  low_stock: 'renewal_due',
  critical: 'critical',
  expired: 'expired',
};

const STATUS_LABEL_MAP: Record<string, string> = {
  available: 'Available',
  low_stock: 'Low Stock',
  critical: 'Critical',
  expired: 'Expired',
};

const EXPORT_COLUMNS = [
  { key: 'itemCode', label: 'Item Code' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'quantity', label: 'Qty' },
  { key: 'unit', label: 'Unit' },
  { key: 'minStock', label: 'Min Stock' },
  { key: 'expiryDate', label: 'Expiry Date' },
  { key: 'location', label: 'Location' },
  { key: 'status', label: 'Status' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'lotNo', label: 'Lot No.' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getExpiryLabel(dateStr: string): { label: string; color: string } {
  const days = differenceInDays(parseISO(dateStr), new Date());
  if (days < 0) return { label: 'Expired', color: 'text-red-600' };
  if (days <= 30) return { label: `${days}d left`, color: 'text-red-600' };
  if (days <= 90) return { label: `${days}d left`, color: 'text-amber-600' };
  return { label: format(parseISO(dateStr), 'dd MMM yyyy'), color: 'text-text-muted' };
}

// ─── Add Item Form State ──────────────────────────────────────────────────────

interface AddItemForm {
  name: string;
  category: string;
  quantity: string;
  unit: string;
  minStock: string;
  expiryDate: string;
  location: string;
  supplier: string;
  lotNo: string;
  storageTemp: string;
}

const BLANK_FORM: AddItemForm = {
  name: '',
  category: 'Reagent',
  quantity: '',
  unit: 'g',
  minStock: '',
  expiryDate: '',
  location: '',
  supplier: '',
  lotNo: '',
  storageTemp: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

function InventoryManagement() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<AddItemForm>(BLANK_FORM);

  // ── Data fetch ──────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['inventory', { search, category, status: statusFilter, page }],
    queryFn: () =>
      api
        .get('/inventory', {
          params: { search, category, status: statusFilter, page, limit: 20 },
        })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_INVENTORY, total: MOCK_INVENTORY.length })),
    placeholderData: { data: MOCK_INVENTORY, total: MOCK_INVENTORY.length },
  });

  const items: InventoryItem[] = data?.data ?? MOCK_INVENTORY;
  const total: number = data?.total ?? MOCK_INVENTORY.length;

  // ── Filtered (client-side on mock) ─────────────────────────────────────────
  const filtered = items.filter((item) => {
    const matchSearch =
      !search ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.itemCode.toLowerCase().includes(search.toLowerCase()) ||
      item.lotNo.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !category || item.category === category;
    const matchStatus = !statusFilter || item.status === statusFilter;
    return matchSearch && matchCategory && matchStatus;
  });

  // ── Stats ───────────────────────────────────────────────────────────────────
  const allItems = MOCK_INVENTORY;
  const totalItems = allItems.length;
  const lowStockCount = allItems.filter((i) => i.status === 'low_stock').length;
  const expiryAlertCount = allItems.filter((i) => {
    const days = differenceInDays(parseISO(i.expiryDate), new Date());
    return days >= 0 && days <= 90;
  }).length;
  const expiredCount = allItems.filter((i) => i.status === 'expired').length;

  // ── Export ──────────────────────────────────────────────────────────────────
  const exportData = filtered.map((item) => ({
    ...item,
  })) as unknown as Record<string, unknown>[];

  const { exportCSV, exportPDF } = useTableExport(exportData, EXPORT_COLUMNS, 'inventory-list');

  // ── Table columns ───────────────────────────────────────────────────────────
  const columns: Column<InventoryItem>[] = [
    {
      key: 'itemCode',
      label: 'Item Code',
      sortable: true,
      width: '110px',
      render: (row) => (
        <span className="font-mono text-xs text-text-muted">{row.itemCode}</span>
      ),
    },
    {
      key: 'name',
      label: 'Name / Category',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm text-text">{row.name}</p>
          <p className="text-xs text-text-dim mt-0.5">{row.category}</p>
        </div>
      ),
    },
    {
      key: 'quantity',
      label: 'Quantity',
      sortable: true,
      align: 'right',
      width: '110px',
      render: (row) => (
        <div className="text-right">
          <span className="font-semibold text-sm text-text">{row.quantity.toLocaleString()}</span>
          <span className="text-xs text-text-muted ml-1">{row.unit}</span>
        </div>
      ),
    },
    {
      key: 'minStock',
      label: 'Min Stock',
      align: 'right',
      width: '100px',
      render: (row) => (
        <div className="text-right">
          <span className="text-sm text-text-muted">{row.minStock.toLocaleString()}</span>
          <span className="text-xs text-text-dim ml-1">{row.unit}</span>
        </div>
      ),
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      sortable: true,
      width: '130px',
      render: (row) => {
        const { label, color } = getExpiryLabel(row.expiryDate);
        return <span className={`text-sm font-medium ${color}`}>{label}</span>;
      },
    },
    {
      key: 'location',
      label: 'Location',
      width: '170px',
      render: (row) => (
        <span className="text-xs text-text-muted">{row.location}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (row) => (
        <PillBadge
          value={STATUS_PILL_MAP[row.status] ?? row.status}
          label={STATUS_LABEL_MAP[row.status] ?? row.status}
          showDot
        />
      ),
    },
  ];

  // ── Drawer detail row ───────────────────────────────────────────────────────
  function DetailRow({
    icon: Icon,
    label,
    value,
  }: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
  }) {
    return (
      <div className="flex gap-3 py-3 border-b border-border last:border-0">
        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-bg flex-shrink-0 mt-0.5">
          <Icon size={14} className="text-text-muted" />
        </span>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-dim mb-0.5">
            {label}
          </p>
          <p className="text-sm text-text">{value}</p>
        </div>
      </div>
    );
  }

  // ── Add form handler ────────────────────────────────────────────────────────
  function handleAddField(field: keyof AddItemForm, value: string) {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleAddSubmit() {
    // In production this would call api.post('/inventory', ...) and invalidate query
    setShowAddModal(false);
    setAddForm(BLANK_FORM);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <PageHeader
        title="Inventory"
        subtitle="Track reagents, reference standards, solvents, and consumables across all storage locations."
        actions={
          <>
            <button className="btn-ghost flex items-center gap-1.5 text-sm" onClick={exportCSV}>
              <Download size={14} />
              CSV
            </button>
            <button className="btn-ghost flex items-center gap-1.5 text-sm" onClick={exportPDF}>
              <FileText size={14} />
              PDF
            </button>
            <button
              className="btn-primary flex items-center gap-1.5 text-sm"
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={14} />
              Add Item
            </button>
          </>
        }
      />

      {/* ── Stats Row ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Package size={18} className="text-blue-600" />
          </span>
          <div>
            <p className="text-2xl font-bold text-text">{totalItems}</p>
            <p className="text-xs text-text-muted">Total Items</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <ArrowDown size={18} className="text-amber-600" />
          </span>
          <div>
            <p className="text-2xl font-bold text-amber-600">{lowStockCount}</p>
            <p className="text-xs text-text-muted">Low Stock</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Clock size={18} className="text-orange-600" />
          </span>
          <div>
            <p className="text-2xl font-bold text-orange-600">{expiryAlertCount}</p>
            <p className="text-xs text-text-muted">Expiry Alerts ≤90d</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <XCircle size={18} className="text-red-600" />
          </span>
          <div>
            <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
            <p className="text-xs text-text-muted">Expired</p>
          </div>
        </div>
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="card p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-text-muted flex-shrink-0" />
          <input
            className="input flex-1 text-sm"
            placeholder="Search name, code or lot number…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="select text-sm min-w-[160px]"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          className="select text-sm min-w-[140px]"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        {(search || category || statusFilter) && (
          <button
            className="btn-ghost text-xs flex items-center gap-1"
            onClick={() => {
              setSearch('');
              setCategory('');
              setStatusFilter('');
              setPage(1);
            }}
          >
            <AlertTriangle size={12} />
            Clear
          </button>
        )}

        <span className="text-xs text-text-muted ml-auto">
          {filtered.length} of {total} items
        </span>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────────── */}
      <div className="card p-0 overflow-hidden">
        <DataTable<InventoryItem>
          columns={columns}
          data={filtered}
          loading={isLoading}
          onRowClick={(row) => setSelectedItem(row)}
          page={page}
          total={filtered.length}
          pageSize={20}
          onPageChange={setPage}
          keyField="id"
          tableId="inventory-table"
          emptyMessage="No inventory items match your filters."
        />
      </div>

      {/* ── Item Detail Drawer ────────────────────────────────────────────── */}
      {selectedItem && (
        <SideDrawer
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          title={selectedItem.name}
          subtitle={`${selectedItem.itemCode} · ${selectedItem.category}`}
          width={500}
        >
          {/* Status badge */}
          <div className="mb-4">
            <PillBadge
              value={STATUS_PILL_MAP[selectedItem.status] ?? selectedItem.status}
              label={STATUS_LABEL_MAP[selectedItem.status] ?? selectedItem.status}
              showDot
            />
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="card p-3 text-center">
              <p className="text-lg font-bold text-text">
                {selectedItem.quantity.toLocaleString()}
                <span className="text-sm font-normal text-text-muted ml-1">{selectedItem.unit}</span>
              </p>
              <p className="text-[11px] text-text-dim uppercase tracking-wide">In Stock</p>
            </div>
            <div className="card p-3 text-center">
              <p className="text-lg font-bold text-text">
                {selectedItem.minStock.toLocaleString()}
                <span className="text-sm font-normal text-text-muted ml-1">{selectedItem.unit}</span>
              </p>
              <p className="text-[11px] text-text-dim uppercase tracking-wide">Min Stock</p>
            </div>
            <div className="card p-3 text-center">
              {(() => {
                const { label, color } = getExpiryLabel(selectedItem.expiryDate);
                return (
                  <>
                    <p className={`text-base font-bold ${color}`}>{label}</p>
                    <p className="text-[11px] text-text-dim uppercase tracking-wide">Expiry</p>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Detail rows */}
          <div className="divide-y divide-border">
            <DetailRow icon={Hash} label="Lot Number" value={selectedItem.lotNo} />
            <DetailRow icon={User} label="Supplier" value={selectedItem.supplier} />
            <DetailRow icon={MapPin} label="Storage Location" value={selectedItem.location} />
            <DetailRow icon={Thermometer} label="Storage Temperature" value={selectedItem.storageTemp} />
            <DetailRow
              icon={Calendar}
              label="Last Received Date"
              value={format(parseISO(selectedItem.lastReceivedDate), 'dd MMM yyyy')}
            />
            <DetailRow
              icon={Calendar}
              label="Expiry Date"
              value={format(parseISO(selectedItem.expiryDate), 'dd MMM yyyy')}
            />
            <DetailRow
              icon={ArrowDown}
              label="Reorder Threshold"
              value={`${selectedItem.minStock} ${selectedItem.unit}`}
            />
            {selectedItem.usageNotes && (
              <DetailRow icon={StickyNote} label="Usage Notes" value={selectedItem.usageNotes} />
            )}
          </div>
        </SideDrawer>
      )}

      {/* ── Add Item Modal ────────────────────────────────────────────────── */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddForm(BLANK_FORM);
        }}
        title="Add Inventory Item"
        subtitle="Register a new reagent, standard, solvent, or consumable."
        size="lg"
        footer={
          <>
            <button
              className="btn-ghost text-sm"
              onClick={() => {
                setShowAddModal(false);
                setAddForm(BLANK_FORM);
              }}
            >
              Cancel
            </button>
            <button className="btn-primary text-sm" onClick={handleAddSubmit}>
              Add Item
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          {/* Name */}
          <div className="col-span-2">
            <label className="label">
              <Beaker size={12} className="inline mr-1" />
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              className="input w-full mt-1"
              placeholder="e.g. Acetonitrile HPLC Grade"
              value={addForm.name}
              onChange={(e) => handleAddField('name', e.target.value)}
            />
          </div>

          {/* Category */}
          <div>
            <label className="label">Category</label>
            <select
              className="select w-full mt-1"
              value={addForm.category}
              onChange={(e) => handleAddField('category', e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Unit */}
          <div>
            <label className="label">Unit</label>
            <select
              className="select w-full mt-1"
              value={addForm.unit}
              onChange={(e) => handleAddField('unit', e.target.value)}
            >
              {['mL', 'L', 'g', 'kg', 'mg', 'pcs', 'pkg'].map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="label">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              className="input w-full mt-1"
              type="number"
              min="0"
              placeholder="0"
              value={addForm.quantity}
              onChange={(e) => handleAddField('quantity', e.target.value)}
            />
          </div>

          {/* Min Stock */}
          <div>
            <label className="label">Min Stock (Reorder Threshold)</label>
            <input
              className="input w-full mt-1"
              type="number"
              min="0"
              placeholder="0"
              value={addForm.minStock}
              onChange={(e) => handleAddField('minStock', e.target.value)}
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="label">Expiry Date</label>
            <input
              className="input w-full mt-1"
              type="date"
              value={addForm.expiryDate}
              onChange={(e) => handleAddField('expiryDate', e.target.value)}
            />
          </div>

          {/* Location */}
          <div>
            <label className="label">Storage Location</label>
            <select
              className="select w-full mt-1"
              value={addForm.location}
              onChange={(e) => handleAddField('location', e.target.value)}
            >
              <option value="">Select location…</option>
              {[
                'Cold Room -20°C',
                'Flammables Cabinet',
                'Chemical Store A-3',
                'Refrigerator 2-8°C',
                'Dry Store B-1',
              ].map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          {/* Supplier */}
          <div>
            <label className="label">Supplier</label>
            <input
              className="input w-full mt-1"
              placeholder="e.g. Merck KGaA"
              value={addForm.supplier}
              onChange={(e) => handleAddField('supplier', e.target.value)}
            />
          </div>

          {/* Lot No */}
          <div>
            <label className="label">Lot / Batch Number</label>
            <input
              className="input w-full mt-1"
              placeholder="e.g. K53021847"
              value={addForm.lotNo}
              onChange={(e) => handleAddField('lotNo', e.target.value)}
            />
          </div>

          {/* Storage Temp */}
          <div className="col-span-2">
            <label className="label">Storage Temperature</label>
            <input
              className="input w-full mt-1"
              placeholder="e.g. 2–8°C or 15–25°C"
              value={addForm.storageTemp}
              onChange={(e) => handleAddField('storageTemp', e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default InventoryManagement;
